import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../utils/api";

export interface Testimonial {
  id: string;
  author: string;
  region: string;
  review: string;
}

interface TestimonialPayload {
  author: string;
  region: string;
  review: string;
}

interface TestimonialState {
  loading: boolean;
  success: boolean;
  error: string | null;
  testimonials: Testimonial[];
}


const initialState: TestimonialState = {
  loading: false,
  success: false,
  error: null,
  testimonials: [],
};

export const addTestimonial = createAsyncThunk(
  "testimonial/add",
  async (data: TestimonialPayload, { rejectWithValue }) => {
    try {
      const res = await api.post(
        `/common/testimonial/add-new`,
        data
      );
      return res.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to add testimonial"
      );
    }
  }
);


export const getAllTestimonials = createAsyncThunk(
  "testimonial/getAll",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get(
        `/common/testimonial/get-all`
      );

      // handleApiSuccess -> { data, message, success }
      return res.data.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch testimonials"
      );
    }
  }
);


const testimonialSlice = createSlice({
  name: "testimonial",
  initialState,
  reducers: {
    resetTestimonialState: (state) => {
      state.loading = false;
      state.success = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      /* ---- ADD ---- */
      .addCase(addTestimonial.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addTestimonial.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(addTestimonial.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      /* ---- GET ALL ---- */
      .addCase(getAllTestimonials.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllTestimonials.fulfilled, (state, action) => {
        state.loading = false;
        state.testimonials = action.payload;
      })
      .addCase(getAllTestimonials.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

/* =======================
   Exports
======================= */

export const { resetTestimonialState } = testimonialSlice.actions;
export default testimonialSlice.reducer;
